import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { Soco } from '~api/soco';
import {
  getNotificationItems,
  mapNotificationsByTab,
} from '@native-components/notification-center/utils';
import {
  NOTIFICATION_PAGE_LIMIT,
  UNREAD_COUNT_CACHE_TTL_MS,
} from './constants';

const NOTIFICATION_MAX_AGE_DAYS = 90;
const getNotificationDateCutoff = () =>
  dayjs()
    .subtract(NOTIFICATION_MAX_AGE_DAYS, 'day')
    .startOf('day')
    .toISOString();

// Maps lilla tab key → API notification_category value
const TAB_TO_CATEGORY = {
  order: 'pesanan',
  loyalty: 'info', // loyalty notifications are stored with notification_category:'info' in DB
  info: 'info',
};

const DEFAULT_LIMIT = NOTIFICATION_PAGE_LIMIT;
const DEFAULT_UNREAD_COUNT_FILTER = {
  notification_category: 'all',
  seen: false,
};

const parseUnreadCount = (payload) => {
  const numericPayload = Number(payload);
  if (Number.isFinite(numericPayload) && numericPayload >= 0) {
    return numericPayload;
  }

  const seenCount = Number(payload?.seen);
  if (Number.isFinite(seenCount) && seenCount >= 0) {
    return seenCount;
  }

  if (payload && typeof payload === 'object') {
    const groupedKeys = [
      'order',
      'pesanan',
      'promo',
      'event',
      'info',
      'loyalty',
    ];
    const groupedTotal = groupedKeys.reduce((total, key) => {
      const current = Number(payload?.[key]);
      if (Number.isFinite(current) && current > 0) {
        return total + current;
      }

      return total;
    }, 0);

    if (groupedTotal > 0) {
      return groupedTotal;
    }
  }

  return 0;
};

// Maps API category fields to lilla tab unread counts
// Both 'loyalty' and 'info' tabs use the 'info' API category
const parseUnreadCountByTab = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { order: 0, loyalty: 0, info: 0 };
  }
  return {
    order: Math.max(0, Number(payload.pesanan) || 0),
    loyalty: Math.max(0, Number(payload.info) || 0),
    info: Math.max(0, Number(payload.info) || 0),
  };
};

const getTotalCountByHeaders = (headers = {}) => {
  const rawValue = headers?.['x-items-count'] || headers?.['X-Items-Count'];
  const totalCount = Number(rawValue);

  if (Number.isFinite(totalCount) && totalCount >= 0) {
    return totalCount;
  }

  return null;
};

const buildNotificationPayload = (response, params = {}) => {
  const finalSkip = Number(params?.skip) || 0;
  const finalLimit = Number(params?.limit) || DEFAULT_LIMIT;
  const notificationsByTab = mapNotificationsByTab(response?.data);
  const notificationItems = getNotificationItems(response?.data);
  const totalItems = getTotalCountByHeaders(response?.headers);
  const fetchedCount = notificationItems.length;
  const nextSkip = finalSkip + fetchedCount;
  let hasMore = fetchedCount >= finalLimit;

  if (totalItems !== null) {
    hasMore = nextSkip < totalItems;
  }

  // Stop pagination immediately when API returns no items
  // (guards against stale or inconsistent total-count headers).
  if (fetchedCount === 0 || nextSkip <= finalSkip) {
    hasMore = false;
  }

  return {
    notificationsByTab,
    fetchedCount,
    nextSkip,
    totalItems,
    limit: finalLimit,
    hasMore,
  };
};

export const getNotifications = createAsyncThunk(
  'notificationCenter/getNotifications',
  async (params = {}) => {
    const requestParams = {
      skip: 0,
      limit: DEFAULT_LIMIT,
      filter: JSON.stringify({
        type: 'lilla',
        notification_category: 'all',
        created_at: { $gte: getNotificationDateCutoff() },
      }),
      sort: '-created_at',
      ...params,
    };

    const response = await Soco.getNotifications(requestParams);
    return buildNotificationPayload(response, requestParams);
  },
);

export const loadMoreNotifications = createAsyncThunk(
  'notificationCenter/loadMoreNotifications',
  async (params = {}) => {
    const requestParams = {
      skip: 0,
      limit: DEFAULT_LIMIT,
      filter: JSON.stringify({
        type: 'lilla',
        notification_category: 'all',
        created_at: { $gte: getNotificationDateCutoff() },
      }),
      sort: '-created_at',
      ...params,
    };
    const response = await Soco.getNotifications(requestParams);
    return buildNotificationPayload(response, requestParams);
  },
);

export const getUnreadNotificationsCount = createAsyncThunk(
  'notificationCenter/getUnreadNotificationsCount',
  async (params = {}) => {
    const requestParams = {
      filter: {
        ...DEFAULT_UNREAD_COUNT_FILTER,
        ...(params?.filter || {}),
      },
      ...params,
    };
    const response = await Soco.getNotificationsUnreadCount(requestParams);
    const data = response?.data?.data;
    return {
      total: parseUnreadCount(data),
      byTab: parseUnreadCountByTab(data),
    };
  },
  {
    condition: (params = {}, { getState }) => {
      const notificationCenterState = getState()?.notificationCenter;
      const statusApiUnreadCount =
        notificationCenterState?.statusApiUnreadCount;
      const unreadCountFetchedAt =
        notificationCenterState?.unreadCountFetchedAt;

      if (statusApiUnreadCount === 'pending') {
        return false;
      }

      if (params?.force) {
        return true;
      }

      if (!unreadCountFetchedAt) {
        return true;
      }

      return Date.now() - unreadCountFetchedAt > UNREAD_COUNT_CACHE_TTL_MS;
    },
  },
);

// Server field → Lilla tab key mapping (only tabs that have a backend field)
const SERVER_TO_TAB = {
  enable_pesanan: 'order',
  enable_info: 'info',
};

// Lilla tab key → server field mapping
const TAB_TO_SERVER = {
  order: 'enable_pesanan',
  info: 'enable_info',
};

export const fetchNotificationPreferences = createAsyncThunk(
  'notificationCenter/fetchNotificationPreferences',
  async () => {
    const response = await Soco.getNotificationPreferences();
    const data = response?.data?.data || response?.data || {};

    // Only return the tabs we know how to map
    const result = {};
    for (const [serverKey, tabKey] of Object.entries(SERVER_TO_TAB)) {
      if (typeof data[serverKey] === 'boolean') {
        result[tabKey] = data[serverKey];
      }
    }
    return result;
  },
  {
    condition: (_, { getState }) => {
      const isLoggedIn = getState()?.auth?.isLoggedIn;
      return Boolean(isLoggedIn);
    },
  },
);

export const updateNotificationPreference = createAsyncThunk(
  'notificationCenter/updateNotificationPreference',
  async ({ key, value }) => {
    const serverKey = TAB_TO_SERVER[key];
    if (!serverKey) {
      // No server field for this tab (e.g. loyalty) — nothing to persist
      return null;
    }
    await Soco.updateNotificationPreferences({ [serverKey]: value });
    return null;
  },
  {
    condition: (_, { getState }) => {
      const isLoggedIn = getState()?.auth?.isLoggedIn;
      return Boolean(isLoggedIn);
    },
  },
);

const buildTabFilter = (tab) => {
  const filter = {
    type: 'lilla',
    notification_category: TAB_TO_CATEGORY[tab] || tab,
    created_at: { $gte: getNotificationDateCutoff() },
  };

  return filter;
};

export const getNotificationsByTab = createAsyncThunk(
  'notificationCenter/getNotificationsByTab',
  async ({ tab, skip = 0, limit = DEFAULT_LIMIT } = {}) => {
    const tabFilter = buildTabFilter(tab);
    const requestParams = {
      skip,
      limit,
      // Stringify so nested $in operators survive axios query-string serialization
      filter: JSON.stringify(tabFilter),
      sort: '-created_at',
    };
    const response = await Soco.getNotifications(requestParams);
    return {
      tab,
      ...buildNotificationPayload(response, { ...requestParams, limit }),
    };
  },
);

export const loadMoreNotificationsByTab = createAsyncThunk(
  'notificationCenter/loadMoreNotificationsByTab',
  async ({ tab, skip = 0, limit = DEFAULT_LIMIT } = {}) => {
    const tabFilter = buildTabFilter(tab);
    const requestParams = {
      skip,
      limit,
      // Stringify so nested $in operators survive axios query-string serialization
      filter: JSON.stringify(tabFilter),
    };
    const response = await Soco.getNotifications(requestParams);
    return {
      tab,
      ...buildNotificationPayload(response, { ...requestParams, limit }),
    };
  },
);

export const markNotificationAsSeen = createAsyncThunk(
  'notificationCenter/markNotificationAsSeen',
  async (notificationId) => {
    await Soco.markNotificationSeen(notificationId);
    return notificationId;
  },
);

export const markAllNotificationsAsSeen = createAsyncThunk(
  'notificationCenter/markAllNotificationsAsSeen',
  async (params = {}) => {
    const requestParams = {
      filter: {
        type: 'lilla',
        notification_category: 'all',
      },
      ...params,
    };

    await Soco.markAllNotificationsAsSeen(requestParams);
    return true;
  },
);
